import React, { createContext, useCallback, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiClient } from '../api/client';
import {
  clearSession,
  readStoredSession,
  setSignOutHandler,
  storeSession,
  storedToken,
} from '../api/session';

interface User {
  id: number;
  email: string;
  token: string;
}

/**
 * `checking`: a stored token is being validated with `GET /auth/me`; protected
 * routes show a spinner, never the signed-in UI (spec 001).
 */
export type AuthStatus = 'checking' | 'signedIn' | 'signedOut';

interface AuthContextType {
  user: User | null;
  status: AuthStatus;
  /** True only while the stored session is being checked (`status === 'checking'`). */
  isLoading: boolean;
  /** The last session check failed on the network; the user stays signed in. */
  serverUnreachable: boolean;
  retry: () => void;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/** Where a forced sign-out sends the user, remembering where they were. */
export function expiredSignInPath(from: string): string {
  return `/auth?expired=1&from=${encodeURIComponent(from)}`;
}

/** The host picker (spec 032) signs in inline and never leaves for `/auth`. */
export function isPickerPath(pathname: string): boolean {
  return pathname.replace(/\/+$/, '') === '/picker';
}

interface AuthProviderProps {
  children: ReactNode;
}

/** Must be rendered inside the router: a forced sign-out navigates. */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState<User | null>(null);
  const [status, setStatus] = useState<AuthStatus>(() => (storedToken() ? 'checking' : 'signedOut'));
  const [serverUnreachable, setServerUnreachable] = useState(false);
  const [checkNonce, setCheckNonce] = useState(0);

  const hereRef = useRef('/');
  hereRef.current = location.pathname + location.search;
  const pathRef = useRef('/');
  pathRef.current = location.pathname;

  // A 401 anywhere ends the session: api/session.ts has already cleared the
  // keys and reset the stores; this updates React state and sends the user to
  // sign in, remembering where they were. The host picker (spec 032 D-2) stays
  // put and shows its own inline form: leaving would lose its origin and kinds.
  useEffect(() => {
    setSignOutHandler(() => {
      if (!isPickerPath(pathRef.current)) {
        navigate(expiredSignInPath(hereRef.current), { replace: true });
      }
      setUser(null);
      setStatus('signedOut');
      setServerUnreachable(false);
    });
    return () => setSignOutHandler(null);
  }, [navigate]);

  // Validate the stored token once on start (and on Retry). Idempotent under
  // StrictMode: the first run's request is aborted and its result discarded.
  useEffect(() => {
    const stored = readStoredSession();
    if (!stored) return;
    const controller = new AbortController();

    apiClient
      .getMe({ signal: controller.signal })
      .then((res) => {
        if (controller.signal.aborted) return;
        if (res.success) {
          const confirmed = { token: stored.token, id: res.data.id, email: res.data.email };
          storeSession(confirmed);
          setUser(confirmed);
          setStatus('signedIn');
          setServerUnreachable(false);
        } else if (!storedToken()) {
          // 401: the session handler has signed out and navigated already.
        } else {
          // Any other failure (5xx, HTML from a proxy) is the server, not the session.
          setUser(stored);
          setStatus('signedIn');
          setServerUnreachable(true);
        }
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        // Network error: stay signed in on the stored session, say so, offer Retry.
        setUser(stored);
        setStatus('signedIn');
        setServerUnreachable(true);
      });

    return () => controller.abort();
  }, [checkNonce]);

  const retry = useCallback(() => setCheckNonce((n) => n + 1), []);

  const startSession = useCallback((data: { id: number; email: string; token: string }) => {
    storeSession(data);
    setUser({ id: data.id, email: data.email, token: data.token });
    setStatus('signedIn');
    setServerUnreachable(false);
  }, []);

  const login = useCallback(
    async (email: string, password: string) => {
      const response = await apiClient.login(email, password);
      if (!response.success) {
        throw new Error(response.error || 'Login failed');
      }
      startSession(response.data);
    },
    [startSession]
  );

  const register = useCallback(
    async (email: string, password: string) => {
      const response = await apiClient.register(email, password);
      if (!response.success) {
        throw new Error(response.error || 'Registration failed');
      }
      startSession(response.data);
    },
    [startSession]
  );

  const logout = useCallback(() => {
    clearSession();
    navigate('/auth');
    setUser(null);
    setStatus('signedOut');
    setServerUnreachable(false);
  }, [navigate]);

  const value: AuthContextType = {
    user,
    status,
    isLoading: status === 'checking',
    serverUnreachable,
    retry,
    login,
    register,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      {serverUnreachable && status === 'signedIn' && (
        <div className="sm-server-banner" role="status">
          <span>Can&apos;t reach the server</span>
          <button type="button" className="sm-server-banner__retry" onClick={retry}>
            Retry
          </button>
        </div>
      )}
    </AuthContext.Provider>
  );
};
